import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { Download } from "lucide-react";
import { useCopy } from "../i18n";

const EASE = [0.16, 1, 0.3, 1];

const Line = ({ index, children }) => (
  <span className="-mb-[0.1em] block overflow-hidden pb-[0.1em]">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, ease: EASE, delay: 0.25 + index * 0.15 }}
    >
      {children}
    </motion.span>
  </span>
);

const fadeUp = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.9, ease: EASE, delay },
});

export const Hero = () => {
  const copy = useCopy();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, 50]);
  const rx = useSpring(0, { stiffness: 120, damping: 16 });
  const ry = useSpring(0, { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    ry.set(((e.clientX - r.left) / r.width - 0.5) * 8);
    rx.set(-((e.clientY - r.top) / r.height - 0.5) * 8);
  };
  const resetTilt = () => {
    rx.set(0);
    ry.set(0);
  };

  return (
    <section id="top" ref={ref} className="relative overflow-hidden">
      <div className="pointer-events-none absolute -top-40 right-[-12%] h-[620px] w-[620px] rounded-full bg-[#B89762]/10 blur-[130px]" />
      <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 px-6 pb-16 pt-28 sm:pt-36 lg:grid-cols-12 lg:gap-10 lg:pb-24">
        <motion.div style={{ y: yText }} className="lg:col-span-7 lg:pr-8">
          <motion.p
            {...fadeUp(0.1)}
            className="mb-8 flex items-center gap-4 text-xs font-medium uppercase tracking-[0.25em] text-[#1E2A38]/70"
          >
            <span className="h-px w-10 bg-[#B89762]" />
            {copy.hero.eyebrow}
          </motion.p>
          <h1 className="font-serif-display text-[clamp(3.4rem,9vw,7rem)] font-light leading-[0.98] tracking-tight text-[#121316]">
            <Line index={0}>
              {copy.hero.line1a}
              <em className="italic text-[#B89762]">{copy.hero.line1em}</em>
              {copy.hero.line1b}
            </Line>
            {copy.hero.line2 && <Line index={1}>{copy.hero.line2}</Line>}
          </h1>
          <motion.p {...fadeUp(0.7)} className="mt-8 max-w-xl text-base leading-relaxed text-[#525866] sm:text-lg">
            {copy.hero.introA}
            <strong className="font-medium text-[#121316]">Stefano Plaia</strong>
            {copy.hero.introB}
            <strong className="font-medium text-[#121316]">{copy.hero.introC}</strong>
            {copy.hero.introD}
          </motion.p>
          <motion.div {...fadeUp(0.85)} className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="#contatti"
              data-testid="hero-cta-button"
              className="rounded-full bg-[#1E2A38] px-8 py-3.5 text-sm font-medium text-[#FBFBF9] transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#121316] hover:shadow-[0_16px_30px_-12px_rgba(30,42,56,0.5)]"
            >
              {copy.hero.ctaPrimary}
            </a>
            <a
              href="#servizi"
              data-testid="hero-secondary-cta"
              className="rounded-full border border-[rgba(18,19,22,0.15)] px-8 py-3.5 text-sm font-medium text-[#121316] transition-all duration-300 hover:border-[#B89762] hover:text-[#B89762]"
            >
              {copy.hero.ctaSecondary}
            </a>
            <a
              href="/Stefano_Plaia_CV.pdf"
              download="Stefano_Plaia_CV.pdf"
              data-testid="hero-cv-download"
              className="inline-flex items-center gap-2 rounded-full px-4 py-3.5 text-sm font-medium text-[#1E2A38] transition-colors duration-300 hover:text-[#B89762]"
            >
              <Download className="h-4 w-4" />
              {copy.hero.cv}
            </a>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: yImg }} className="lg:col-span-5">
          <motion.div {...fadeUp(0.55)} style={{ perspective: 1000 }} className="relative mx-auto w-full max-w-sm pb-6 lg:max-w-none">
            <div className="absolute -inset-3 rounded-t-[10rem] rounded-b-2xl border border-[#B89762]/40" />
            <motion.div
              data-testid="hero-portrait-frame"
              onMouseMove={onTilt}
              onMouseLeave={resetTilt}
              style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }}
              className="relative overflow-hidden rounded-t-[10rem] rounded-b-2xl bg-[#F4F3EF] shadow-[0_40px_80px_-30px_rgba(30,42,56,0.35)]"
            >
              <img
                src="/stefano.png"
                alt="Stefano Plaia"
                data-testid="hero-portrait"
                className="aspect-[4/5] w-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E2A38]/25 via-transparent to-transparent" />
            </motion.div>
            <div
              data-testid="hero-availability-badge"
              className="absolute -bottom-0 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-[rgba(18,19,22,0.08)] bg-[#FBFBF9]/90 px-5 py-2 text-xs tracking-wide text-[#1E2A38] shadow-sm backdrop-blur"
            >
              <span className="mr-2 inline-block h-2 w-2 rounded-full bg-emerald-500 align-middle" />
              {copy.hero.badge}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
